// FILE: src/components/ProfilePage.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import "./ProfilePage.css";
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Lock as LockIcon,
  ExitToApp as LogoutIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import defaultAvatar from "../images/download.png";

// This component receives all its data via props from its parent (e.g., RMDashboard).
const ProfilePage = ({ profile, profilePhotoUrl, onProfileUpdate }) => {
  const navigate = useNavigate();

  // State for the temporary file preview
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // State for UI feedback
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for the leave request dialog
  const [leaveRequestDialogOpen, setLeaveRequestDialogOpen] = useState(false);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    reason: "",
    start_date: "",
    end_date: "",
  });
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);

  // Uses URL.createObjectURL for a fast, local preview
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      // 2MB validation
      setSnackbar({
        open: true,
        message: "File size must be under 2MB.",
        severity: "error",
      });
      return;
    }
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setSnackbar({
        open: true,
        message: "Only JPG and PNG files are allowed.",
        severity: "error",
      });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // Simplified upload logic
  const handlePhotoUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);

    const formData = new FormData();
    formData.append("profilePhoto", selectedFile);

    try {
      await api.post("/api/users/me/profile-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSnackbar({
        open: true,
        message: "Profile photo updated successfully!",
        severity: "success",
      });

      if (onProfileUpdate) {
        onProfileUpdate();
      }

      cancelUpload();
    } catch (error) {
      console.error("Error uploading photo:", error);
      setSnackbar({
        open: true,
        message: "Error uploading photo.",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const cancelUpload = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    const input = document.getElementById("photo-upload-input");
    if (input) input.value = "";
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDeletePhoto = async () => {
    if (!window.confirm("Are you sure you want to delete your profile photo?"))
      return;
    setIsUploading(true);
    try {
      await api.delete("/api/users/me/profile-photo");
      setSnackbar({
        open: true,
        message: "Profile photo deleted.",
        severity: "success",
      });
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      console.error("Error deleting photo:", error);
      setSnackbar({
        open: true,
        message: "Error deleting photo.",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitLeaveRequest = async () => {
    // Enhanced validation
    if (!leaveRequestForm.reason.trim() || leaveRequestForm.reason.trim().length < 10) {
      setSnackbar({
        open: true,
        message: "Reason must be at least 10 characters long.",
        severity: "warning",
      });
      return;
    }
    if (!leaveRequestForm.start_date) {
      setSnackbar({
        open: true,
        message: "Start date is required.",
        severity: "warning",
      });
      return;
    }

    // Validate date logic
    const startDate = new Date(leaveRequestForm.start_date);
    const endDate = leaveRequestForm.end_date ? new Date(leaveRequestForm.end_date) : null;

    if (endDate && endDate < startDate) {
      setSnackbar({
        open: true,
        message: "End date cannot be before start date.",
        severity: "warning",
      });
      return;
    }

    setIsSubmittingLeave(true);

    // Prepare data for API - ensure dates are in correct format
    const requestData = {
      reason: leaveRequestForm.reason.trim(),
      start_date: leaveRequestForm.start_date,
      end_date: leaveRequestForm.end_date || null, // Send null if empty
    };

    try {
      await api.post("/api/users/me/leave-request", requestData);
      setSnackbar({
        open: true,
        message: "Leave request submitted successfully!",
        severity: "success",
      });
      setLeaveRequestDialogOpen(false);
      setLeaveRequestForm({ reason: "", start_date: "", end_date: "" });
    } catch (error) {
      // Show detailed error message from backend
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors?.[0]?.message ||
                          "Error submitting leave request.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.clear();
      navigate("/user-login");
    }
  };

  const imageSrc = previewUrl || profilePhotoUrl || defaultAvatar;

  return (
    <div className="profile-page-container">
      <h1 className="profile-header">Settings</h1>

      <div className="settings-panel">
        <div className="settings-panel-header">
          <img
            src={imageSrc}
            alt="Profile"
            className="photo-preview"
            onError={(e) => (e.target.src = defaultAvatar)}
          />
          <div className="profile-info">
            <h2>{profile?.name || "N/A"}</h2>
            <p>
              {profile?.email || "N/A"} •{" "}
              {profile?.role
                ?.replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A"}
            </p>
          </div>
        </div>

        <div className="action-row">
          <div className="action-details">
            <h3>Profile Picture</h3>
            <p>Update your profile picture. Max size: 2MB, JPG/PNG.</p>
          </div>
          <div className="action-buttons">
            {isUploading ? (
              <CircularProgress size={24} />
            ) : selectedFile ? (
              <>
                <Button
                  onClick={handlePhotoUpload}
                  variant="contained"
                  color="primary"
                >
                  Save
                </Button>
                <Button onClick={cancelUpload} variant="outlined">
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  htmlFor="photo-upload-input"
                >
                  Choose Photo
                  <input
                    type="file"
                    hidden
                    accept="image/png, image/jpeg"
                    onChange={handleFileChange}
                    id="photo-upload-input"
                  />
                </Button>
                {profilePhotoUrl && profilePhotoUrl !== defaultAvatar && (
                  <Button
                    onClick={handleDeletePhoto}
                    variant="outlined"
                    color="error"
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="action-row">
          <div className="action-details">
            <h3>Password</h3>
            <p>Change your password to keep your account secure.</p>
          </div>
          <div className="action-buttons">
            <Button
              variant="outlined"
              onClick={() => navigate("/change-password")}
            >
              Change Password
            </Button>
          </div>
        </div>

        <div className="action-row">
          <div className="action-details">
            <h3>Leave Management</h3>
            <p>Submit a request for upcoming leave for manager approval.</p>
          </div>
          <div className="action-buttons">
            <Button
              variant="outlined"
              onClick={() => setLeaveRequestDialogOpen(true)}
            >
              Submit Request
            </Button>
          </div>
        </div>
      </div>

      <div className="logout-section">
        <button className="logout-button" onClick={handleLogout}>
          <LogoutIcon fontSize="small" /> Log Out
        </button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={leaveRequestDialogOpen}
        onClose={() => setLeaveRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Leave Request</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            value={leaveRequestForm.reason}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                reason: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            multiline
            rows={3}
            variant="outlined"
          />
          <TextField
            label="Start Date"
            type="date"
            value={leaveRequestForm.start_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                start_date: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={leaveRequestForm.end_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                end_date: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRequestDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitLeaveRequest}
            variant="contained"
            disabled={isSubmittingLeave}
          >
            {isSubmittingLeave ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
